<template>
	<div>
		<el-dialog custom-class="tdt-map-dialog" width="660px" title="选择经纬度（天地图）" :visible.sync="innerVisible"
			append-to-body :close-on-click-modal="false" @opened="handleOpened" @close="closeMap">
			<div class="map-wrapper">
				<div class="map-search">
					<el-input v-model="keyword" size="small" clearable placeholder="输入地址搜索，例如：贵阳市南明区XX路"
						@keyup.enter.native="handleSearch">
						<el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
					</el-input>
				</div>
				<div :id="mapBoxId" ref="mapBox" class="map-box" :style="{ height: mapHeight + 'px' }"
					v-loading="mapLoading" element-loading-text="天地图加载中..."></div>
				<div class="map-tip">
					<span class="tip-address" v-if="address">已选：{{ address }}</span>
					<span class="tip-address tip-placeholder" v-else>请在地图上点击选择位置</span>
					<span class="tip-coord" v-if="lng !== null && lat !== null">{{ lng }}, {{ lat }}</span>
				</div>
			</div>
			<div slot="footer" class="dialog-footer">
				<el-button @click="onClose">取 消</el-button>
				<el-button type="primary" @click="onSubmit">确定</el-button>
			</div>
		</el-dialog>
	</div>
</template>

<script>
	import { loadTianditu } from '../utils/tianditu'

	export default {
		name: 'MapSelect',
		data() {
			return {
				mapBoxId: 'tdtMapBox' + Math.random().toString(36).slice(2, 8),
				innerVisible: false,
				keyword: '',
				address: '',
				lng: null,
				lat: null,
				mapLoading: false,
				mapHeight: 420
			}
		},
		created() {
			// 不放进 data，避免 Vue 劫持地图实例导致页面卡顿
			this.map = null
			this.marker = null
			this.geocoder = null
		},
		mounted() {
			window.addEventListener('resize', this.handleWindowResize)
		},
		beforeDestroy() {
			window.removeEventListener('resize', this.handleWindowResize)
			this.map = null
			this.marker = null
			this.geocoder = null
		},
		methods: {
			/* ==================== 对外 API，与原来保持一致 ==================== */
			show(options = {}) {
				this.keyword = ''
				this.address = options.address || ''

				const lng = Number(options.lng)
				const lat = Number(options.lat)
				const hasLng = options.lng !== '' && options.lng !== null && options.lng !== undefined && !isNaN(lng)
				const hasLat = options.lat !== '' && options.lat !== null && options.lat !== undefined && !isNaN(lat)

				this.lng = (hasLng && hasLat) ? lng : null
				this.lat = (hasLng && hasLat) ? lat : null

				// 打开前先按当前窗口算好地图高度，避免弹窗总高溢出屏幕
				this.mapHeight = this.calcMapHeight()

				this.innerVisible = true
				this.$nextTick(() => this.initMap())
			},
			closeMap() {
				// 地图实例保留复用，重新打开时只做重新定位
			},
			onClose() {
				this.innerVisible = false
			},
			onSubmit() {
				if (this.lng === null || this.lat === null) {
					this.$message.warning('请在地图上选择地址')
					return
				}
				this.innerVisible = false
				// 载荷结构与原来腾讯 locpicker 完全一致，Form.vue 无需改动
				this.$emit('confrimAdress', {
					latlng: {
						lat: this.lat,
						lng: this.lng
					},
					poiaddress: this.address
				})
			},

			/* ==================== 尺寸自适应 ==================== */
			// 弹窗除地图外的固定占用：上下外边距 + 标题 + 搜索栏 + 提示行 + 按钮栏 + 内边距
			calcMapHeight() {
				const winH = window.innerHeight || document.documentElement.clientHeight || 800
				const chrome =
					Math.round(winH * 0.10) + // 弹窗上下外边距 + 余量
					48 + // 标题栏
					42 + // 搜索栏 + 间距
					30 + // 坐标提示行
					62 + // 按钮栏
					16 // body 内边距
				// 下限 260 保证能用，上限 860 避免大屏上地图过高
				return Math.max(260, Math.min(winH - chrome, 860))
			},
			handleOpened() {
				// 弹窗展开动画结束后再校准一次尺寸
				this.mapHeight = this.calcMapHeight()
				this.$nextTick(() => this.resizeMap())
			},
			handleWindowResize() {
				if (!this.innerVisible) return
				const h = this.calcMapHeight()
				if (h === this.mapHeight) return
				this.mapHeight = h
				this.$nextTick(() => this.resizeMap())
			},
			resizeMap() {
				if (this.map && this.map.checkResize) {
					this.map.checkResize()
				}
			},

			/* ==================== 地图初始化 ==================== */
			async initMap() {
				if (!this.$refs.mapBox) return
				this.mapLoading = true
				try {
					await loadTianditu()
					if (!this.map) {
						this.createMap()
					} else {
						this.resizeMap()
					}
					this.applyInitial()
				} catch (e) {
					this.$message.error(e.message || '地图加载失败')
				} finally {
					this.mapLoading = false
				}
			},
			createMap() {
				const T = window.T
				this.map = new T.Map(this.$refs.mapBox)
				this.map.centerAndZoom(new T.LngLat(116.397428, 39.90923), 12)
				this.map.enableScrollWheelZoom()
				this.map.addEventListener('click', this.handleMapClick)
				this.geocoder = new T.Geocoder()
				// 刚插入 DOM 时容器尺寸可能还没稳定，稍后再校准一次
				setTimeout(this.resizeMap, 350)
			},
			applyInitial() {
				if (!this.map) return
				if (this.lng !== null && this.lat !== null) {
					this.placeMarker(this.lng, this.lat, true)
					if (!this.address) this.reverseGeocode(new window.T.LngLat(this.lng, this.lat))
				} else if (this.address) {
					this.geocodeAddress(this.address, true)
				}
			},

			/* ==================== 打点与逆地理编码 ==================== */
			handleMapClick(e) {
				const lnglat = e.lnglat || e.lonlat
				if (!lnglat || !this.map) return
				this.placeMarker(lnglat.getLng(), lnglat.getLat(), false)
				this.reverseGeocode(lnglat)
			},
			placeMarker(lng, lat, moveCenter) {
				const T = window.T
				if (!T || !this.map) return
				const lnglat = new T.LngLat(Number(lng), Number(lat))
				if (this.marker) this.map.removeOverLay(this.marker)
				this.marker = new T.Marker(lnglat)
				this.map.addOverLay(this.marker)
				if (moveCenter) this.map.centerAndZoom(lnglat, 16)
				this.lng = Number(Number(lng).toFixed(6))
				this.lat = Number(Number(lat).toFixed(6))
			},
			reverseGeocode(lnglat) {
				if (!window.T) return
				if (!this.geocoder) this.geocoder = new window.T.Geocoder()
				this.geocoder.getLocation(lnglat, (result) => {
					if (result && typeof result.getStatus === 'function' && result.getStatus() === 0) {
						this.address = result.getAddress() || ''
					} else {
						this.address = ''
					}
				})
			},
			geocodeAddress(addr, onlyCenter) {
				if (!addr || !window.T) return
				if (!this.geocoder) this.geocoder = new window.T.Geocoder()
				this.geocoder.getPoint(addr, (result) => {
					if (!result || typeof result.getStatus !== 'function' || result.getStatus() !== 0) {
						this.$message.warning('没有找到该地址，请换个关键词或直接在地图上点击')
						return
					}
					const loc = result.getLocationPoint()
					if (!loc) return
					this.placeMarker(loc.getLng(), loc.getLat(), true)
					if (onlyCenter || !this.address) this.address = addr
				})
			},
			handleSearch() {
				const kw = (this.keyword || '').trim()
				if (!kw) {
					this.$message.warning('请输入要搜索的地址')
					return
				}
				if (!this.map) {
					this.$message.warning('地图还没加载完成，请稍候')
					return
				}
				this.geocodeAddress(kw, false)
			}
		}
	}
</script>

<style scoped>
	.map-wrapper {
		display: flex;
		flex-direction: column;
	}

	.map-search {
		flex: 0 0 auto;
		margin-bottom: 8px;
	}

	.map-box {
		/* 关键：天地图内部图层是 position:absolute，容器必须定位，
		   否则图层会以 .el-dialog 为基准铺满整个弹窗，盖住标题和按钮 */
		position: relative;
		flex: 0 0 auto;
		width: 100%;
		overflow: hidden;
		background: #f5f7fa;
	}

	.map-tip {
		flex: 0 0 auto;
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-top: 8px;
		font-size: 13px;
		line-height: 20px;
		color: #303133;
	}

	.tip-address {
		flex: 1 1 auto;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.tip-placeholder {
		color: #909399;
	}

	.tip-coord {
		flex: 0 0 auto;
		color: #909399;
		margin-left: 10px;
		white-space: nowrap;
	}

	.dialog-footer {
		text-align: right;
	}
</style>

<!-- custom-class 挂在 el-dialog 根节点上，拿不到 scoped 的 data-v 属性，所以这部分必须非 scoped -->
<style>
	.tdt-map-dialog {
		display: flex;
		flex-direction: column;
		margin: 4vh auto 0 !important;
		max-height: 96vh;
		overflow: hidden;
	}

	.tdt-map-dialog .el-dialog__header {
		flex: 0 0 auto;
		padding: 16px 20px 8px;
	}

	.tdt-map-dialog .el-dialog__body {
		flex: 1 1 auto;
		min-height: 0;
		overflow: hidden;
		padding: 0 20px;
	}

	.tdt-map-dialog .el-dialog__footer {
		flex: 0 0 auto;
		padding: 8px 20px 16px;
	}
</style>
